import { Marker, Popup } from 'react-leaflet'
import {
  CategoryResponse,
  EventResponse,
  ExtendedEvent,
} from '../../../../constants/types'
import { PopupText } from '../PopupText/PopupText'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { getMarkerIcon } from '../../../../utils/markers/getMarkerIcon'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'
import { addEllipsisEnding } from '../../../../utils/strings/addEllipsisEnding'
import { useActions } from '../../../../hooks/redux/actions'
import { useEffect, useRef, useState } from 'react'
import {
  getParsedDate,
  getParsedTime,
} from '../../../../utils/common/parseDateTime'
import { getLanguageByCode } from '../../../../utils/common/getLanguages'
import { useGetCategoriesQuery } from '../../../../store'
import { showLeavePointCreationModal } from '../../../../utils/modals/showLeavePointCreationModal'
import { useUuiContext, UuiContexts } from '@epam/uui-core'
import './EditableMarkerEvent.scss'

const MAX_NUM_LETTERS_TO_DISPLAY = 60

export const EditableMarkerEvent = (eventObj: ExtendedEvent) => {
  const initialPosition = { lat: eventObj.lat, lng: eventObj.lng } as L.LatLng
  const [position, setPosition] = useState(initialPosition)
  const { data: categories } = useGetCategoriesQuery()
  const markerRef = useRef(null)
  const { isEditEventFormOpened, isNewEventFormOpened } = useAppSelector(
    (state) => state.app
  )
  const { selectedEventId } = useAppSelector((state) => state.events)
  const { showEventInfo } = useSideBarControlls()

  const { setEditEventPosition } = useActions()
  const svc = useUuiContext<UuiContexts>()
  const isEventSelected = selectedEventId === eventObj.id

  const getExtendedEvent = (
    event: EventResponse,
    categories: CategoryResponse[] = []
  ) => {
    const category = categories?.find((cat) => cat.id === event.categoryId)
    const eventExtention = {
      parsedTime: getParsedTime(event.time),
      parsedDate: getParsedDate(event.time),
      location: '',
      categoryName: category?.name ?? 'Unknown Category',
      languageName: getLanguageByCode(event.language?.toLowerCase()).name,
    }
    return { ...event, ...eventExtention }
  }

  useEffect(() => {
    if (isEditEventFormOpened && isEventSelected) {
      setEditEventPosition(position)
    }
  }, [
    position,
    isEditEventFormOpened,
    // setEditEventPosition
  ])

  useEffect(() => {
    setPosition(initialPosition)
  }, [isEventSelected, isEditEventFormOpened])

  return (
    <Marker
      key={eventObj.id}
      icon={getMarkerIcon(
        !!(selectedEventId === eventObj.id),
        `event${eventObj.categoryId}`
      )}
      position={position}
      draggable={isEditEventFormOpened && selectedEventId === eventObj.id}
      ref={markerRef}
      eventHandlers={{
        click: () => {
          if (!isEventSelected) {
            if (isNewEventFormOpened || isEditEventFormOpened) {
              showLeavePointCreationModal(svc).then(
                () => {
                  showEventInfo(getExtendedEvent(eventObj, categories))
                },
                () => {}
              )
            } else {
              showEventInfo(getExtendedEvent(eventObj, categories))
            }
          }
        },
        mouseover: (event) => event.target.openPopup(),
        mouseout: (event) => event.target.closePopup(),
        dragend: (event) => {
          const marker = event.target
          if (marker != null) {
            setPosition({
              lat: marker._latlng.lat,
              lng: marker._latlng.lng,
            } as L.LatLng)
          }
        },
      }}
      opacity={selectedEventId && selectedEventId !== eventObj.id ? 0.5 : 1}
    >
      <Popup closeButton={false}>
        <PopupText>
          {addEllipsisEnding(eventObj.name, MAX_NUM_LETTERS_TO_DISPLAY)}
        </PopupText>
      </Popup>
    </Marker>
  )
}
